import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';

export type ThemeMode = 'light' | 'dark';

/** Base look of the administration: the redesign or the classic look of the old administration */
export type BaseTheme = 'new' | 'classic';

@Injectable()
export class ThemeService {
  private readonly THEME_KEY = 'selected-theme';
  private readonly BASE_THEME_KEY = 'selected-base-theme';
  /** instance default from the config (ui.theme.default) */
  private defaultBaseTheme: BaseTheme = 'new';
  private themeMode = new BehaviorSubject<ThemeMode>(this.getStoredTheme());
  private baseTheme = new BehaviorSubject<BaseTheme>(this.getStoredBaseTheme());
  private css: string;
  private cssLoadingFailed = false;
  private cssFilePath: string;
  private logo: string;
  private title: string;
  private favicon: string;

  public constructor(private http: HttpClient) {
    this.applyTheme(this.themeMode.value);
    this.applyBaseTheme(this.baseTheme.value);
  }

  public load(cssFilePath: string) {
    this.cssFilePath = cssFilePath;

    return new Promise<void>((resolve) => {
      this.http.get(cssFilePath, {responseType: 'text' })
        .subscribe(
          response => {
            this.css = <string>response;
            resolve();
          },
          error => {
            this.cssLoadingFailed = true;
            resolve();
          });
    });
  }

  public getCss() {
    return this.css;
  }

  public getCssFilePath() {
    return this.cssFilePath;
  }

  public setLogo(logo: string) {
    this.logo = logo;
  }

  public getLogo() {
    return this.logo;
  }

  public setTitle(title: string) {
    this.title = title;
  }

  public getTitle() {
    return this.title;
  }

  public setFavicon(favicon: string) {
    this.favicon = favicon;
  }

  public getFavicon() {
    return this.favicon;
  }

  public hasFailedToLoadCss() {
    return this.cssLoadingFailed;
  }

  public getThemeMode(): Observable<ThemeMode> {
    return this.themeMode.asObservable();
  }

  public toggleTheme(): void {
    const newTheme = this.themeMode.value === 'light' ? 'dark' : 'light';
    this.setThemeMode(newTheme);
  }

  public setThemeMode(theme: ThemeMode): void {
    localStorage.setItem(this.THEME_KEY, theme);
    this.themeMode.next(theme);
    this.applyTheme(theme);
  }

  public getStoredTheme(): ThemeMode {
    return (localStorage.getItem(this.THEME_KEY) as ThemeMode) || 'light';
  }

  public getBaseTheme(): Observable<BaseTheme> {
    return this.baseTheme.asObservable();
  }

  /** The choice of this browser (test routes module/theme/classic and module/theme/new), otherwise the instance default */
  public getStoredBaseTheme(): BaseTheme {
    const stored = localStorage.getItem(this.BASE_THEME_KEY);
    return stored === 'classic' || stored === 'new' ? stored : this.defaultBaseTheme;
  }

  /** Sets the instance default from the config (ui.theme.default) */
  public setDefaultBaseTheme(theme: string): void {
    this.defaultBaseTheme = theme === 'classic' ? 'classic' : 'new';
    this.baseTheme.next(this.getStoredBaseTheme());
    this.applyBaseTheme(this.baseTheme.value);
  }

  public setBaseTheme(theme: BaseTheme): void {
    localStorage.setItem(this.BASE_THEME_KEY, theme);
    this.baseTheme.next(theme);
    this.applyBaseTheme(theme);
  }

  /**
   * The classic look is a stylesheet next to the theme css (theme-classic.css). It is loaded on demand and
   * after the theme, so its rules win over the theme rules of the same specificity
   */
  public applyBaseTheme(theme: BaseTheme = this.baseTheme.value): void {
    document.body.classList.toggle('theme-classic', theme === 'classic');

    if (theme !== 'classic' || !this.cssFilePath || document.getElementById('theme-classic-css')) {
      return;
    }

    const link = document.createElement('link');
    link.id = 'theme-classic-css';
    link.rel = 'stylesheet';
    link.href = this.cssFilePath.replace(/[^/]*$/, 'theme-classic.css');
    document.head.appendChild(link);
  }

  private applyTheme(theme: ThemeMode): void {
    document.body.classList.remove('light-theme', 'dark-theme');
    document.body.classList.add(`${theme}-theme`);
  }
}
